import React, { useState, useEffect } from 'react';
import AdminLayout, { adminFetch, KpiCard, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

/**
 * AdminClients — page admin (Phase 6 / G2 — migration endpoint TS).
 *
 * Backend : `GET /api/admin/leads?limit=...` retourne `{ items: LeadDto[],
 * nextCursor, total }`. Le shape `LeadDto` est plus pauvre que le legacy
 * `clients/*.json` (pas d'historique d'appels, pas d'aides recommandées
 * détaillées) — ces informations sont désormais portées par les contextes
 * `dossier`, `matching`, `interview`, `chat`. Pour la vue admin globale,
 * on liste juste les leads avec leur statut + profil minimal.
 *
 * Le legacy avait : prenom/nom/email/telephone/profil/aides_recommandees/
 * historique_appels. Le nouveau a : nom (concaténé)/email/telephone/source/
 * statut/assignedAgentId/profil(LeadProfilDto)/dossierIds/notes/dates.
 *
 * Pagination cursor (Phase 6 / G1) — bouton "Charger plus" à la fin.
 */
export default function AdminClients() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [nextCursor, setCursor] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [query, setQuery]       = useState('');
  const { dark, border } = useAdmin();
  const text = dark ? '#e0e0e0' : '#2D2D2D';
  const muted = '#8A8A8A';

  const load = (cursor = null) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    setLoadError(false);
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}&limit=20` : '?limit=20';
    adminFetch(`/api/admin/leads${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const list = Array.isArray(data?.items) ? data.items : [];
        setItems(prev => cursor ? [...prev, ...list] : list);
        setTotal(typeof data?.total === 'number' ? data.total : list.length);
        setCursor(data?.nextCursor || null);
      })
      .catch(() => { if (!cursor) setItems([]); setLoadError(true); })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter(c => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [c.nom, c.telephone, c.email].filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const stats = {
    total,
    nouveaux: items.filter(c => c.statut === 'nouveau').length,
    en_cours: items.filter(c => c.statut === 'en_cours').length,
    qualifies: items.filter(c => c.statut === 'qualifié').length,
  };

  const statutColor = (statut) => {
    switch (statut) {
      case 'nouveau':   return '#2980B9';
      case 'en_cours':  return '#F0A500';
      case 'qualifié':  return '#3D8B5E';
      case 'clôturé':   return '#8A8A8A';
      default:          return '#8A8A8A';
    }
  };

  return (
    <AdminLayout title="Leads & clients">
      {loading ? (
        <p style={{ color: muted, padding: 16 }}>Chargement…</p>
      ) : loadError && items.length === 0 ? (
        <div style={{
          padding: 16, borderRadius: 10, background: '#FDECEA',
          border: '1px solid rgba(192,57,43,0.3)', color: '#933',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span>Erreur de chargement. Vérifiez votre connexion.</span>
          <button onClick={() => load()} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Réessayer</button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
            <KpiCard label="Total leads" value={stats.total} icon="📋" color="#D4724A" />
            <KpiCard label="Nouveaux (page)" value={stats.nouveaux} icon="🆕" color="#2980B9" />
            <KpiCard label="En cours (page)" value={stats.en_cours} icon="⏳" color="#F0A500" />
            <KpiCard label="Qualifiés (page)" value={stats.qualifies} icon="✅" color="#3D8B5E" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
            {/* Liste */}
            <AdminCard>
              <SectionTitle>Leads ({items.length} chargés / {total} au total)</SectionTitle>
              <input
                type="text" placeholder="Rechercher par nom, téléphone, email…" value={query}
                onChange={e => setQuery(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${border}`, background: dark ? '#0f0f23' : '#fff',
                  color: text, fontSize: 13, boxSizing: 'border-box', marginBottom: 12, outline: 'none',
                }}
              />
              {filtered.length === 0 ? (
                <p style={{ color: muted, fontSize: 14 }}>Aucun lead trouvé.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 520, overflowY: 'auto' }}>
                  {filtered.map(c => (
                    <div key={c.id} onClick={() => setSelected(c)} style={{
                      padding: '10px 14px', borderRadius: 10,
                      border: `1px solid ${selected?.id === c.id ? '#D4724A' : border}`,
                      cursor: 'pointer', background: selected?.id === c.id ? '#FFF0EA' : 'transparent',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: selected?.id === c.id ? '#D4724A' : text, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{c.nom || 'Sans nom'}</span>
                        <Badge color={statutColor(c.statut)}>{c.statut}</Badge>
                      </div>
                      <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                        {[c.telephone, c.email, c.source && `via ${c.source}`].filter(Boolean).join(' · ')}
                        {' · '}{new Date(c.updatedAt).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {nextCursor && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <button onClick={() => load(nextCursor)} disabled={loadingMore} style={{
                    padding: '8px 18px', borderRadius: 8, border: '1px solid #D9D9D9',
                    background: loadingMore ? '#F5F3F0' : 'transparent', color: '#D4724A',
                    fontSize: 13, fontWeight: 600, cursor: loadingMore ? 'not-allowed' : 'pointer',
                  }}>
                    {loadingMore ? 'Chargement…' : 'Charger plus'}
                  </button>
                </div>
              )}
            </AdminCard>

            {/* Détail */}
            {selected && (
              <AdminCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <SectionTitle>{selected.nom || 'Sans nom'}</SectionTitle>
                  <button onClick={() => setSelected(null)} style={{
                    padding: '4px 10px', borderRadius: 6, border: `1px solid ${border}`,
                    background: 'transparent', color: muted, fontSize: 12, cursor: 'pointer',
                  }}>✕</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 16 }}>
                  {[
                    ['Statut', selected.statut || '—'],
                    ['Source', selected.source || '—'],
                    ['Téléphone', selected.telephone || '—'],
                    ['Email', selected.email || '—'],
                    ['Agent assigné', selected.assignedAgentId || '—'],
                    ['Lead ID', selected.id],
                    ['Âge (profil)', selected.profil?.age ? `${selected.profil.age} ans` : '—'],
                    ['Département', selected.profil?.departement || '—'],
                    ['GIR', selected.profil?.girNiveau ? `GIR ${selected.profil.girNiveau}` : '—'],
                    ['Logement', selected.profil?.situationLogement || '—'],
                    ['Handicap', selected.profil?.handicap ? 'Oui' : '—'],
                    ['ALD', selected.profil?.ald ? 'Oui' : '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ padding: '6px 0', borderBottom: `1px solid ${border}` }}>
                      <div style={{ fontSize: 10, color: muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
                      <div style={{ color: text, fontWeight: 500, wordBreak: 'break-word' }}>{v}</div>
                    </div>
                  ))}
                </div>

                {selected.profil?.besoins?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                      Besoins ({selected.profil.besoins.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selected.profil.besoins.map((b, i) => (
                        <span key={i} style={{
                          padding: '3px 10px', borderRadius: 20, background: '#FFF0EA',
                          color: '#D4724A', fontSize: 12, fontWeight: 500,
                        }}>{b}</span>
                      ))}
                    </div>
                  </div>
                )}

                {selected.dossierIds?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                      Dossiers liés ({selected.dossierIds.length})
                    </div>
                    {selected.dossierIds.map((id) => (
                      <div key={id} style={{
                        padding: '6px 10px', borderRadius: 7, background: dark ? '#0f0f23' : '#F5F3F0',
                        marginBottom: 4, fontSize: 12, color: text, fontFamily: 'monospace',
                      }}>
                        {id}
                      </div>
                    ))}
                  </div>
                )}

                {selected.notes?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                      Notes ({selected.notes.length})
                    </div>
                    {selected.notes.map((n, i) => (
                      <div key={i} style={{
                        padding: '7px 10px', borderRadius: 7, background: dark ? '#0f0f23' : '#F5F3F0',
                        marginBottom: 4, fontSize: 12, color: text, lineHeight: 1.5,
                      }}>
                        {n}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 16, fontSize: 11, color: muted, textAlign: 'right' }}>
                  Créé le {new Date(selected.createdAt).toLocaleString('fr-FR')}
                  {' · '}
                  Mis à jour le {new Date(selected.updatedAt).toLocaleString('fr-FR')}
                </div>
              </AdminCard>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
